import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkout } from '../context/useWorkout';
import { soundManager } from '../utils/SoundManager';

// Import all Japan trainer data
import japanOneTrainersData from '../data/JapanOne';
import japanTwoTrainersData from '../data/JapanTwo';
import japanThreeTrainersData from '../data/JapanThree';
import japanFourTrainersData from '../data/JapanFour';
import japanFiveTrainersData from '../data/JapanFive';

// Import Japan-specific CSS
import './JapanOne.css';

const JapanTrainerPage = () => {
    const { trainerId, programId } = useParams();
    const navigate = useNavigate();
    const { actions } = useWorkout();

    const [trainer, setTrainer] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [showIntensitySelection, setShowIntensitySelection] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);

    // Combine all Japan trainer data
    const allJapanTrainers = {
        ...japanOneTrainersData,
        ...japanTwoTrainersData,
        ...japanThreeTrainersData,
        ...japanFourTrainersData,
        ...japanFiveTrainersData
    };

    useEffect(() => {
        console.log('🇯🇵 Japan Trainer Page - Looking for trainer:', trainerId);
        console.log('🔍 Available trainers:', Object.keys(allJapanTrainers));

        const foundTrainer = allJapanTrainers[trainerId];
        if (foundTrainer) {
            console.log('✅ Found Japan trainer:', foundTrainer.name);
            setTrainer(foundTrainer);

            // If programId is provided, find and select that program
            if (programId) {
                const program = foundTrainer.programs.find(p => p.id === programId);
                if (program) {
                    console.log('🎯 Found program:', program.name);
                    setSelectedProgram(program);
                    setShowIntensitySelection(true);
                }
            }
        } else {
            console.error('❌ Japan trainer not found:', trainerId);
        }

        // Initialize or get current round from localStorage
        const savedRound = localStorage.getItem('currentGameRound');
        if (savedRound) {
            setCurrentRound(parseInt(savedRound, 10));
        }
    }, [trainerId, programId]);

    console.log('🔍 Japan Debug state:', {
        trainerId,
        programId,
        selectedProgram: selectedProgram?.name,
        showIntensitySelection,
        currentRound
    });

    // Handle program selection
    const handleProgramSelect = (program) => {
        console.log('🎯 Japan Program selected:', program.name);

        // 🎵 PROGRAM SELECTION SOUND
        soundManager.playButtonSound();

        setSelectedProgram(program);
        setShowIntensitySelection(true);

        // Update URL to include program selection
        navigate(`/japan-trainer/${trainerId}/${program.id}/intensity-selection`);
    };

    // Handle intensity selection (start workout)
    const handleIntensitySelect = (intensity) => {
        console.log('🔥 Japan Intensity selected:', intensity);

        // 🎵 INTENSITY SELECTION SOUND
        soundManager.playSound('next22', 0.8);

        if (!selectedProgram) {
            console.error('❌ No program selected');
            return;
        }

        // Determine reps and sets based on intensity and program defaults
        let reps, sets;
        switch(intensity) {
            case 'light':
                reps = Math.max(Math.floor(selectedProgram.reps * 0.7), 3);
                sets = Math.max(Math.floor(selectedProgram.sets * 0.7), 2);
                break;
            case 'moderate':
                reps = selectedProgram.reps;
                sets = selectedProgram.sets;
                break;
            case 'intense':
                reps = Math.floor(selectedProgram.reps * 1.3);
                sets = Math.floor(selectedProgram.sets * 1.2);
                break;
            case 'game':
                // GAME MODE: Progressive reps based on current round
                reps = currentRound; // Round 1 = 1 rep, Round 2 = 2 reps, etc.
                sets = 1; // Always 1 set in game mode
                console.log(`🎮 GAME MODE: Round ${currentRound} = ${reps} reps`);
                break;
            default:
                reps = selectedProgram.reps;
                sets = selectedProgram.sets;
        }

        console.log(`🎌 Starting Japan trainer workout: ${reps} reps × ${sets} sets`);

        // Calculate intensity percentage
        let intensityLevel;
        switch(intensity) {
            case 'light':
                intensityLevel = 70;
                break;
            case 'moderate':
                intensityLevel = 100;
                break;
            case 'intense':
                intensityLevel = 130;
                break;
            case 'game':
                intensityLevel = 100; // Base level for game mode
                break;
            default:
                intensityLevel = 100;
        }

        // Calculate workout metrics
        const exerciseCount = selectedProgram.pattern.length;
        const totalReps = reps * exerciseCount * sets;

        console.log(`🎌 Workout metrics: ${exerciseCount} exercises, ${totalReps} total reps`);

        // Store Japan trainer info in sessionStorage (same as Hero flow)
        const trainerInfo = {
            isTrainerProgram: true,
            isJapanTrainer: true,
            category: 'japan-trainer',
            workoutType: 'japan-trainer',
            trainerName: trainer.name,
            programName: selectedProgram.name,
            intensityLevel: intensityLevel,
            intensityLabel: intensity === 'game' ? `Game Mode - Round ${currentRound}` : intensity.charAt(0).toUpperCase() + intensity.slice(1),
            trainerId: trainer.id,
            programId: selectedProgram.id,
            repCount: reps,
            setCount: sets,
            totalReps: totalReps,
            exerciseCount: exerciseCount,
            isGameMode: intensity === 'game',
            currentRound: intensity === 'game' ? currentRound : null
        };

        console.log('🎌 Storing Japan trainer info:', trainerInfo);
        sessionStorage.setItem('trainerInfo', JSON.stringify(trainerInfo));

        // Set workout context
        actions.setMuscleGroups(selectedProgram.pattern);

        // Navigate to WorkoutSummary for email collection (same as Hero flow)
        navigate('/workout-summary', {
            state: {
                trainerId: trainer.id,
                trainerName: trainer.name,
                programId: selectedProgram.id,
                programName: selectedProgram.name,
                category: 'japan-trainer',
                workoutType: 'japan-trainer',
                isJapanTrainer: true,
                isTrainerProgram: true,
                workoutCode: selectedProgram.pattern,
                reps: reps,
                sets: sets,
                repCount: reps,
                setCount: sets,
                totalReps: totalReps,
                exerciseCount: exerciseCount,
                intensity: intensity,
                intensityLabel: intensity === 'game' ? `Game Mode - Round ${currentRound}` : intensity.charAt(0).toUpperCase() + intensity.slice(1),
                intensityLevel: intensityLevel,
                isGameMode: intensity === 'game',
                currentRound: intensity === 'game' ? currentRound : null
            }
        });
    };

    // Handle advancing to next round (for game mode)
    const handleNextRound = () => {
        const nextRound = currentRound + 1;
        setCurrentRound(nextRound);
        localStorage.setItem('currentGameRound', nextRound.toString());
        console.log(`🎮 Advanced to Round ${nextRound}`);
    };

    // Handle resetting game mode
    const handleResetGame = () => {
        setCurrentRound(1);
        localStorage.setItem('currentGameRound', '1');
        console.log('🎮 Game reset to Round 1');
    };

    // Handle back navigation
    const handleBackToCategories = () => {
        soundManager.playButtonSound();
        navigate('/japan-train-categories');
    };

    const handleBackToTrainer = () => {
        soundManager.playButtonSound();
        setShowIntensitySelection(false);
        setSelectedProgram(null);
        navigate(`/japan-trainer/${trainerId}`);
    };

    if (!trainer) {
        return (
            <div className="japan-trainers-container">
                <div className="japan-animated-background"></div>
                <div className="japan-trainers-header">
                    <h1>Japan Trainer Not Found</h1>
                    <button
                        className="back-to-categories-button"
                        onClick={handleBackToCategories}
                    >
                        ← Back to Categories
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="japan-trainers-container">
            <div className="japan-animated-background"></div>

            {/* Back Button */}
            <button
                className="back-to-categories-button"
                onClick={showIntensitySelection ? handleBackToTrainer : handleBackToCategories}
            >
                ← {showIntensitySelection ? 'Back to Trainer' : 'Back to Categories'}
            </button>

            {!showIntensitySelection ? (
                // Trainer Profile View
                <div className="trainer-profile-section">
                    <div className="japan-trainers-header">
                        <div className="header-content">
                            <h1 style={{ color: trainer.color }}>{trainer.name}</h1>
                            <p className="trainer-specialization">{trainer.specialization}</p>
                            <div className="category-stats">
                                <span className="stat-badge">Experience: {trainer.experience}</span>
                                <span className="stat-badge">Programs: {trainer.programs.length}</span>
                                <span className="stat-badge">Category: Ancient Japan</span>
                            </div>
                        </div>
                    </div>

                    {/* Trainer Image */}
                    <div className="japan-trainer-image-section">
                        <div
                            className="japan-trainer-image"
                            style={{
                                backgroundImage: `url(${trainer.image})`,
                                '--japan-primary-color': trainer.color,
                                '--japan-secondary-color': trainer.secondaryColor
                            }}
                        >
                            <div className="japan-mist-effect"></div>
                            <div className="experience-badge japan-badge">
                                {trainer.experience}
                            </div>
                        </div>
                    </div>

                    {/* Trainer Introduction */}
                    <div className="trainer-intro-section">
                        <div className="japan-divider"></div>
                        <p className="trainer-intro-text">{trainer.intro}</p>
                        <div className="japan-divider"></div>
                    </div>

                    {/* Programs Section */}
                    <div className="programs-section">
                        <h2>Training Programs</h2>
                        <div className="programs-grid">
                            {trainer.programs.map((program, index) => (
                                <div
                                    key={program.id}
                                    className="program-card"
                                    onClick={() => handleProgramSelect(program)}
                                    style={{
                                        '--japan-primary-color': trainer.color,
                                        '--japan-secondary-color': trainer.secondaryColor
                                    }}
                                >
                                    <div className="cherry-blossom-accent">🌸</div>
                                    <h3 className="program-name">{program.name}</h3>
                                    <p className="program-description">{program.description}</p>

                                    <div className="program-stats">
                                        <span className="program-stat">
                                            <strong>Reps:</strong> {program.reps}
                                        </span>
                                        <span className="program-stat">
                                            <strong>Sets:</strong> {program.sets}
                                        </span>
                                        <span className="program-focus">
                                            {program.focus}
                                        </span>
                                    </div>

                                    <button
                                        className="select-japan-trainer-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleProgramSelect(program);
                                        }}
                                        style={{
                                            '--japan-primary-color': trainer.color,
                                            '--japan-secondary-color': trainer.secondaryColor,
                                            marginTop: '15px',
                                            width: '100%'
                                        }}
                                    >
                                        Start Training
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Intensity Selection View
                <div className="intensity-selection-section">
                    <div className="japan-trainers-header">
                        <div className="header-content">
                            <h1 style={{ color: trainer.color }}>{selectedProgram.name}</h1>
                            <p>with {trainer.name}</p>
                            <p className="program-description">{selectedProgram.description}</p>
                        </div>
                    </div>

                    {/* Game Mode Round Display */}
                    <div className="game-mode-status">
                        <div className="current-round-display">
                            <h3>🎮 Current Game Round: {currentRound}</h3>
                            <div className="round-controls">
                                <button className="round-button" onClick={handleNextRound}>
                                    Advance to Round {currentRound + 1}
                                </button>
                                <button className="round-button reset" onClick={handleResetGame}>
                                    Reset to Round 1
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="intensity-selection-grid">
                        <div
                            className="intensity-card light"
                            onClick={() => handleIntensitySelect('light')}
                        >
                            <h3>🌸 Light Training</h3>
                            <p>Perfect for beginners or recovery days</p>
                            <div className="intensity-stats">
                                <span>Reps: {Math.max(Math.floor(selectedProgram.reps * 0.7), 3)}</span>
                                <span>Sets: {Math.max(Math.floor(selectedProgram.sets * 0.7), 2)}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card moderate"
                            onClick={() => handleIntensitySelect('moderate')}
                        >
                            <h3>⚖️ Moderate Training</h3>
                            <p>Standard training as designed by the master</p>
                            <div className="intensity-stats">
                                <span>Reps: {selectedProgram.reps}</span>
                                <span>Sets: {selectedProgram.sets}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card intense"
                            onClick={() => handleIntensitySelect('intense')}
                        >
                            <h3>🔥 Intense Training</h3>
                            <p>For experienced warriors seeking ultimate challenge</p>
                            <div className="intensity-stats">
                                <span>Reps: {Math.floor(selectedProgram.reps * 1.3)}</span>
                                <span>Sets: {Math.floor(selectedProgram.sets * 1.2)}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card game-mode"
                            onClick={() => handleIntensitySelect('game')}
                        >
                            <h3>✨ GAME MODE ✨</h3>
                            <p>Progressive card game: Round {currentRound} = {currentRound} {currentRound === 1 ? 'rep' : 'reps'} per exercise</p>
                            <div className="intensity-stats">
                                <span>Current Round: {currentRound}</span>
                                <span>Reps This Round: {currentRound}</span>
                                <span>Sets: 1</span>
                            </div>
                            <div className="game-mode-preview">
                                <small>Next round will be {currentRound + 1} reps per exercise</small>
                            </div>
                        </div>
                    </div>

                    <div className="selected-program-info">
                        <h3>Program Focus: {selectedProgram.focus}</h3>
                        <p><strong>Training Pattern:</strong> {selectedProgram.pattern}</p>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="japan-trainers-footer">
                <div className="japan-info">
                    <h3>Ancient Japan Training Philosophy</h3>
                    <div className="japan-benefits-grid">
                        <div className="japan-benefit-item">
                            <div className="benefit-icon">🏯</div>
                            <div>
                                <strong>Traditional Wisdom</strong>
                                <p>Training methods refined over centuries of Japanese martial tradition</p>
                            </div>
                        </div>
                        <div className="japan-benefit-item">
                            <div className="benefit-icon">⚔️</div>
                            <div>
                                <strong>Warrior Spirit</strong>
                                <p>Develop the inner strength and discipline of legendary samurai</p>
                            </div>
                        </div>
                        <div className="japan-benefit-item">
                            <div className="benefit-icon">🧘</div>
                            <div>
                                <strong>Mind-Body Unity</strong>
                                <p>Achieve perfect harmony between physical training and spiritual growth</p>
                            </div>
                        </div>
                        <div className="japan-benefit-item">
                            <div className="benefit-icon">🌸</div>
                            <div>
                                <strong>Cultural Heritage</strong>
                                <p>Connect with the rich cultural traditions of ancient Japan</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JapanTrainerPage;